import { categoriesIndexHref } from './category-navigation.ts';
import { RETURN_TO_LIBRARY } from './library-navigation.ts';
import { RETURN_TO_USER, userOriginBack } from './user-detail-navigation.ts';

export const RETURN_TO_CATEGORIES = 'categories';
export const RETURN_TO_GRADEBOOK = 'gradebook';
export const RETURN_TO_H5P = 'h5p';
export const RETURN_TO_DASHBOARD = 'dashboard';
export const FROM_GRADEBOOK = 'gradebook';

const CONTEXT_KEYS = [
  'return_to',
  'category_id',
  'course_search',
  'return',
  'search',
  'status',
  'visibility',
  'level',
  'instructor_id',
  'is_free',
  'trashed',
  'sort',
  'direction',
  'view',
  'page',
  'h5p_id',
  'library_id',
] as const;

const H5P_ORIGIN_KEYS = ['library_id', 'course', 'search', 'type', 'status', 'category_id', 'tag_id'] as const;

export function courseContextSearchParams(searchParams: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams();
  for (const key of CONTEXT_KEYS) {
    const value = searchParams.get(key);
    if (value) next.set(key, value);
  }
  return next;
}

export function courseBackUrl(searchParams: URLSearchParams, categoryId?: string | null): string {
  const returnTo = searchParams.get('return_to');

  if (returnTo === RETURN_TO_CATEGORIES) {
    return categoriesIndexHref(searchParams.get('category_id') ?? categoryId, searchParams.get('course_search'));
  }

  if (returnTo === RETURN_TO_GRADEBOOK) {
    const query = courseContextSearchParams(searchParams);
    query.delete('return_to');
    query.delete('category_id');
    const courseSearch = searchParams.get('course_search');
    if (courseSearch) query.set('search', courseSearch);
    const qs = query.toString();
    return `/calificaciones${qs ? `?${qs}` : ''}`;
  }

  if (returnTo === RETURN_TO_H5P) {
    const h5pId = searchParams.get('h5p_id');
    if (!h5pId) return '/h5p';
    const query = new URLSearchParams();
    const libraryId = searchParams.get('library_id');
    if (libraryId) query.set('return_to', RETURN_TO_LIBRARY);
    for (const key of H5P_ORIGIN_KEYS) {
      const value = searchParams.get(key);
      if (value) query.set(key, value);
    }
    const qs = query.toString();
    return `/h5p/${h5pId}${qs ? `?${qs}` : ''}`;
  }

  const query = courseContextSearchParams(searchParams);
  query.delete('return_to');
  query.delete('course_search');
  const qs = query.toString();
  return `/courses${qs ? `?${qs}` : ''}`;
}

export function gradebookCourseHref(courseId: string, search?: string | null): string {
  const query = new URLSearchParams();
  query.set('return_to', RETURN_TO_GRADEBOOK);
  const term = search?.trim();
  if (term) query.set('search', term);
  return `/calificaciones/curso/${courseId}?${query.toString()}`;
}

export function gradebookStudentHref(courseId: string, studentId: string, searchParams: URLSearchParams): string {
  const query = courseContextSearchParams(searchParams);
  query.set('from', FROM_GRADEBOOK);
  return `/progreso/${studentId}/${courseId}?${query.toString()}`;
}

export function progressOriginBack(
  searchParams: URLSearchParams,
  courseId?: string,
  userId?: string,
): { to: string; label: string } {
  const fallback = userOriginBack(searchParams, '/progreso', 'Volver a Progreso de aprendizaje', userId);
  if (searchParams.get('return_to') === RETURN_TO_USER) return fallback;
  if (searchParams.get('from') === FROM_GRADEBOOK && courseId) {
    const query = courseContextSearchParams(searchParams);
    const qs = query.toString();
    return {
      to: `/calificaciones/curso/${courseId}${qs ? `?${qs}` : ''}`,
      label: 'Volver al libro de calificaciones',
    };
  }
  return fallback;
}

function withCourseContext(path: string, searchParams: URLSearchParams): string {
  const qs = courseContextSearchParams(searchParams).toString();
  return qs ? `${path}?${qs}` : path;
}

export function coursePlayHref(courseId: string, itemId: string, searchParams: URLSearchParams): string {
  return withCourseContext(`/courses/${courseId}/play/${itemId}`, searchParams);
}

export function coursePlayBackUrl(courseId: string, searchParams: URLSearchParams): string {
  return withCourseContext(`/courses/${courseId}/preview`, searchParams);
}

export function courseForumThreadHref(courseId: string, threadId: string, searchParams: URLSearchParams): string {
  return withCourseContext(`/courses/${courseId}/foro/${threadId}`, searchParams);
}

function withDashboardOrigin(path: string, searchParams: URLSearchParams): string {
  if (searchParams.get('return_to') !== RETURN_TO_DASHBOARD) return path;
  return `${path}?return_to=${RETURN_TO_DASHBOARD}`;
}

export function dashboardCourseHref(courseId: string, itemId?: string | null): string {
  const path = itemId ? `/mis-cursos/${courseId}/play/${itemId}` : `/mis-cursos/${courseId}`;
  return `${path}?return_to=${RETURN_TO_DASHBOARD}`;
}

export function studentHubBackUrl(searchParams: URLSearchParams): string {
  if (searchParams.get('return_to') === RETURN_TO_DASHBOARD) return '/dashboard';
  return '/mis-cursos';
}

export function studentCourseBackUrl(courseId: string, searchParams: URLSearchParams): string {
  return withDashboardOrigin(`/mis-cursos/${courseId}`, searchParams);
}

export function studentActivityHref(courseId: string, itemId: string, searchParams: URLSearchParams): string {
  return withDashboardOrigin(`/mis-cursos/${courseId}/play/${itemId}`, searchParams);
}

export function h5pRelatedCourseHref(courseId: string, h5pId: string, searchParams: URLSearchParams): string {
  const query = new URLSearchParams();
  query.set('return_to', RETURN_TO_H5P);
  query.set('h5p_id', h5pId);
  for (const key of H5P_ORIGIN_KEYS) {
    const value = searchParams.get(key);
    if (value) query.set(key, value);
  }
  return `/courses/${courseId}?${query.toString()}`;
}

export type CourseHubTab = 'curso' | 'edicion' | 'detalle' | 'calificaciones' | 'sesion' | 'configuracion' | 'foro';

export interface CourseTabHrefOptions {
  studentHub?: boolean;
}

export function courseTabHref(
  courseId: string,
  tab: CourseHubTab,
  searchParams: URLSearchParams,
  options: CourseTabHrefOptions = {},
): string {
  const query = courseContextSearchParams(searchParams);
  const qs = query.toString();
  const suffix = qs ? `?${qs}` : '';
  const studentHub = options.studentHub ?? false;

  switch (tab) {
    case 'curso':
      return studentHub ? `/mis-cursos/${courseId}${suffix}` : `/courses/${courseId}/preview${suffix}`;
    case 'edicion':
      return `/courses/${courseId}/builder${suffix}`;
    case 'detalle':
      return `/courses/${courseId}${suffix}`;
    case 'calificaciones':
      return studentHub
        ? `/mis-cursos/${courseId}/calificaciones${suffix}`
        : `/calificaciones/curso/${courseId}${suffix}`;
    case 'foro':
      return studentHub ? `/mis-cursos/${courseId}/foro${suffix}` : `/courses/${courseId}/foro${suffix}`;
    case 'sesion':
      return `/courses/${courseId}/horas-sesion${suffix}`;
    case 'configuracion':
      return `/courses/${courseId}/edit${suffix}`;
    default:
      return studentHub ? `/mis-cursos/${courseId}${suffix}` : `/courses/${courseId}${suffix}`;
  }
}
